import { Suspense, useDeferredValue, useEffect } from 'react'
import { createFileRoute, Link, stripSearchParams, useMatch } from '@tanstack/react-router'
import { skipToken, useQuery, useQueryClient, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'server/trpc'
import * as valibot from 'valibot'
import { LIVE_LOG_SPAN_MS, LOG_RANGE_MS, toggleLogFilter } from 'shared/log-query'
import { LOG_RANGES } from 'shared/wide-event-logs'
import { SegmentedControl, SegmentedTab } from '@/components/custom/segmented-control'
import { Badge } from '@/components/ui/badge'
import { trpc } from '@/main'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { cn } from '@/lib/utils'
import { SETTINGS_STACK, SettingsSection } from './-components/settings/settings-section'
import { ActivityIcon } from './-components/settings/settings-icons'
import { ADMIN_TONE } from './-components/admin/admin-pages'
import {
  FacetsSkeleton,
  HistogramSkeleton,
  LOGS_DESCRIPTION,
  LOGS_TITLE,
  LOG_SEARCH_DEFAULTS,
  LOG_SEARCH_FIELDS,
  LogRowsSkeleton,
  LOG_VIEWS,
  LogsPending,
} from './-components/admin/wide-event-logs-pending'
import {
  FacetSidebar,
  formatLogTime,
  LevelHistogram,
  LoadMore,
  LogList,
  LIVE_REFRESH_MS,
  LIVE_TAIL_MS,
  QueryBar,
  QueryProblems,
  QuerySyntaxHint,
  RANGE_LABEL,
  StoreNotice,
  type OnFilter,
} from './-components/admin/wide-event-logs'
import { mergeTail } from './-components/admin/wide-event-logs-tail'
import { SuperAdminBoundary } from './-components/admin/super-admin-boundary'
import { SessionLink } from './-components/admin/traces-shared'

// The log explorer over the wide events: a level histogram to zoom on, facets to filter by, rows to open. The Console
// view narrows it to the console lines of the server (`log`) and the browsers (`client_log`), By session groups those.

export const Route = createFileRoute('/_app/admin/logs')({
  // The view, query, range and zoom are search params: a view is a link, and they key the reads on `_list` below.
  validateSearch: valibot.object({
    ...LOG_SEARCH_FIELDS,
    view: valibot.fallback(valibot.optional(valibot.picklist(LOG_VIEWS), 'all'), 'all'),
  }),
  search: { middlewares: [stripSearchParams(LOG_SEARCH_DEFAULTS)] },
  component: RouteComponent,
  pendingComponent: LogsPending,
  staticData: { fullWidth: true },
})

type ConsoleSession = Extract<
  inferRouterOutputs<AppRouter>['admin']['listConsoleSessions'],
  { store: 'ok' }
>['sessions'][number]

const VIEW_LABEL: Record<(typeof LOG_VIEWS)[number], string> = {
  all: 'All events',
  console: 'Console',
  sessions: 'By session',
}

function RouteComponent() {
  const { view } = Route.useSearch()
  return (
    <div className={SETTINGS_STACK}>
      <SettingsSection icon={ActivityIcon} tone={ADMIN_TONE} title={LOGS_TITLE} description={LOGS_DESCRIPTION}>
        <div className="flex flex-col gap-4">
          <Toolbar />
          <SuperAdminBoundary what="logs">
            <div className="flex flex-col gap-4">
              <Suspense fallback={<HistogramSkeleton />}>
                <Histogram />
              </Suspense>
              <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
                <div className="order-last lg:order-first">
                  <Suspense fallback={<FacetsSkeleton />}>
                    <Facets />
                  </Suspense>
                </div>
                <Suspense fallback={<LogRowsSkeleton />}>{view === 'sessions' ? <Sessions /> : <Rows />}</Suspense>
              </div>
            </div>
          </SuperAdminBoundary>
        </div>
      </SettingsSection>
    </div>
  )
}

function useOnFilter(): OnFilter {
  const navigate = Route.useNavigate()
  return (field, value, exclude) =>
    void navigate({
      search: (prev) => ({
        ...prev,
        query: toggleLogFilter(prev.query, field, value, exclude),
      }),
    })
}

function Toolbar() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const zoomed = search.from !== undefined && search.to !== undefined
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <QueryBar
          query={search.query}
          placeholder={
            search.view === 'all' ? 'level:error route:/jobs* timeout' : 'level:error source:browser session:…'
          }
          onSubmit={(query) => void navigate({ search: (prev) => ({ ...prev, query }) })}
        />
        <SegmentedControl>
          {LOG_VIEWS.map((view) => (
            <SegmentedTab
              key={view}
              from={Route.fullPath}
              search={(prev) => ({ ...prev, view })}
              active={search.view === view}
            >
              {VIEW_LABEL[view]}
            </SegmentedTab>
          ))}
        </SegmentedControl>
        <SegmentedControl>
          {LOG_RANGES.map((range) => (
            <SegmentedTab
              key={range}
              from={Route.fullPath}
              search={(prev) => ({
                ...prev,
                range,
                from: undefined,
                to: undefined,
              })}
              active={!zoomed && search.range === range}
            >
              {RANGE_LABEL[range]}
            </SegmentedTab>
          ))}
        </SegmentedControl>
        <LiveToggle live={search.live} />
        {zoomed && (
          <Link
            from={Route.fullPath}
            search={(prev) => ({ ...prev, from: undefined, to: undefined })}
            className="text-primary text-xs font-medium hover:underline"
          >
            Reset zoom
          </Link>
        )}
      </div>
      <QueryProblems query={search.query} />
      {search.live && (
        <p role="status" className="text-muted-foreground text-xs">
          Live: the last {LOG_RANGE_MS[search.range] < LIVE_LOG_SPAN_MS ? RANGE_LABEL[search.range] : 'hour'} at most,
          new events every {LIVE_TAIL_MS / 1000} s, the chart and facets every {LIVE_REFRESH_MS / 1000} s, without the
          admin screens’ own requests.
        </p>
      )}
      <QuerySyntaxHint />
    </div>
  )
}

function LiveToggle(props: { live: boolean }) {
  return (
    <SegmentedControl>
      <SegmentedTab
        from={Route.fullPath}
        search={(prev) => ({
          ...prev,
          live: !props.live,
          from: undefined,
          to: undefined,
        })}
        active={props.live}
        aria-label={props.live ? 'Stop live tail' : 'Start live tail'}
      >
        <span
          className={cn('size-2 rounded-full', props.live ? 'animate-pulse bg-emerald-500' : 'bg-muted-foreground/40')}
        />
        Live
      </SegmentedTab>
    </SegmentedControl>
  )
}

function Histogram() {
  const { context } = useMatch({ from: '/_app/admin/logs/_list' })
  const { live } = Route.useSearch()
  const navigate = Route.useNavigate()
  const deferred = useDeferredValue(context.logHistogramQueryOptions)
  const { data, isFetching } = useSuspenseQuery({
    ...deferred,
    refetchInterval: live ? LIVE_REFRESH_MS : false,
  })
  if (data.store !== 'ok') return null
  return (
    <LevelHistogram
      data={data}
      stale={(isFetching && !live) || deferred !== context.logHistogramQueryOptions}
      onZoom={(from, to) =>
        void navigate({
          search: (prev) => ({ ...prev, from, to, live: false }),
        })
      }
    />
  )
}

function Facets() {
  const { context } = useMatch({ from: '/_app/admin/logs/_list' })
  const { live, query } = Route.useSearch()
  const onFilter = useOnFilter()
  const deferred = useDeferredValue(context.logFacetsQueryOptions)
  const { data } = useSuspenseQuery({
    ...deferred,
    refetchInterval: live ? LIVE_REFRESH_MS : false,
  })
  if (data.store !== 'ok') return null
  return (
    <FacetSidebar
      facets={data.facets}
      query={query}
      stale={deferred !== context.logFacetsQueryOptions}
      onFilter={onFilter}
    />
  )
}

function Rows() {
  const { context } = useMatch({ from: '/_app/admin/logs/_list' })
  const { live, query, view } = Route.useSearch()
  const onFilter = useOnFilter()
  const deferred = useDeferredValue(context.logRowsInfiniteQueryOptions)
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useSuspenseInfiniteQuery(deferred)
  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  })
  const first = data.pages[0]
  const newest = first?.store === 'ok' ? first.rows[0] : undefined
  useLiveTail(live && deferred === context.logRowsInfiniteQueryOptions ? newest : undefined)
  if (first && first.store !== 'ok')
    return <StoreNotice store={first.store} retrying={isFetching} onRetry={() => void refetch()} />
  const rows = data.pages.flatMap((page) => (page.store === 'ok' ? page.rows : []))
  return (
    <LogList
      rows={rows}
      stale={deferred !== context.logRowsInfiniteQueryOptions}
      empty={
        view === 'all'
          ? 'No events match this query in this window.'
          : 'No console lines match this query in this window.'
      }
      query={query}
      onFilter={onFilter}
      footer={
        <LoadMore
          loadMoreRef={loadMoreRef}
          loading={isFetchingNextPage}
          done={
            !hasNextPage && rows.length >= 1000
              ? 'The first thousand events: narrow the query or zoom to see further.'
              : null
          }
        />
      }
    />
  )
}

/**
 * Polls only past the newest row on screen and merges what comes back into the loaded pages, so a live tail never
 * rescans the window. A tail that comes back full may have skipped rows, so the list then reloads whole.
 */
function useLiveTail(newest: { at: string; id: string } | undefined) {
  const { context } = useMatch({ from: '/_app/admin/logs/_list' })
  const queryClient = useQueryClient()
  // query: direct keyed on the newest row on screen, which only the loaded list knows
  const { data } = useQuery(
    trpc.admin.tailLogs.queryOptions(
      newest ? { ...context.logInput, live: true, after: { at: newest.at, id: newest.id } } : skipToken,
      {
        refetchInterval: LIVE_TAIL_MS,
        // Empty and fresh until the first interval: the row it is keyed on was just read.
        initialData: { store: 'ok', rows: [], full: false },
        staleTime: LIVE_TAIL_MS,
      },
    ),
  )
  const rows = context.logRowsInfiniteQueryOptions
  useEffect(() => {
    if (data?.store !== 'ok' || data.rows.length === 0) return
    if (data.full) void queryClient.refetchQueries({ queryKey: rows.queryKey, exact: true })
    else queryClient.setQueryData(rows.queryKey, (prev) => prev && mergeTail(prev, data.rows))
  }, [data, queryClient, rows])
}

function Sessions() {
  const { context } = useMatch({ from: '/_app/admin/logs/_list' })
  const { live } = Route.useSearch()
  const deferred = useDeferredValue(context.logSessionsQueryOptions)
  const { data, isFetching, refetch } = useSuspenseQuery({
    ...deferred,
    refetchInterval: live ? LIVE_REFRESH_MS : false,
  })
  if (data.store !== 'ok')
    return <StoreNotice store={data.store} retrying={isFetching} onRetry={() => void refetch()} />
  if (data.sessions.length === 0)
    return (
      <p className="text-muted-foreground rounded-xl border p-6 text-sm">
        No browser session wrote to the console in this window. Server lines outside a request have no session: see them
        under Console.
      </p>
    )
  return (
    <ul
      className={cn(
        'flex flex-col divide-y overflow-hidden rounded-xl border transition-opacity',
        deferred !== context.logSessionsQueryOptions && 'opacity-60',
      )}
    >
      {data.sessions.map((session) => (
        <SessionRow key={session.sessionId} session={session} />
      ))}
    </ul>
  )
}

function SessionRow(props: { session: ConsoleSession }) {
  const session = props.session
  return (
    <li className="flex flex-col gap-1.5 px-3 py-2.5 text-xs">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <SessionLink sessionId={session.sessionId} at={session.lastAt} />
        <span className="text-muted-foreground font-mono tabular-nums">
          {formatLogTime(session.firstAt)} → {formatLogTime(session.lastAt).slice(7)}
        </span>
        {session.userId && <span className="text-muted-foreground">user {session.userId}</span>}
        <span className="flex items-center gap-1">
          {session.errors > 0 && <Badge variant="destructive">{session.errors} errors</Badge>}
          {session.warns > 0 && (
            <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-400">
              {session.warns} warnings
            </Badge>
          )}
          <Badge variant="secondary">{session.lines} lines</Badge>
        </span>
        <Link
          from={Route.fullPath}
          search={(prev) => ({
            ...prev,
            view: 'console',
            query: toggleLogFilter(prev.query, 'session', session.sessionId),
          })}
          className="text-primary ml-auto font-medium hover:underline"
        >
          Read its console
        </Link>
      </div>
      <p className="truncate font-mono" title={session.lastMessage}>
        {session.lastMessage}
      </p>
      {session.routes.length > 0 && (
        <p className="text-muted-foreground truncate font-mono">{session.routes.join(' · ')}</p>
      )}
    </li>
  )
}
